import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Player, TeamStatsDoc, ScheduleEvent, PublicContent, SeasonRecapDoc, CurrentLeague, RecentWeek } from '@/types';
import { SEASON, currentSeasonLine, weekDate } from '@/config/season';
import { WeekLines } from '@/components/WeekLines';
import { TrendingUp, TrendingDown, Calendar, ChevronRight, Flame, Megaphone, Sun, Trophy, Sparkles } from 'lucide-react';

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({ label, value, sub }) => (
  <div className="text-center px-4 py-6 bg-willard-black">
    <p className="font-display text-4xl sm:text-5xl text-white leading-none">{value}</p>
    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mt-2">{label}</p>
    {sub && <p className="text-xs text-willard-grey-500 mt-1">{sub}</p>}
  </div>
);

const SectionHead: React.FC<{ icon?: React.ReactNode; eyebrow: string; title: string; sub?: string }> = ({
  icon,
  eyebrow,
  title,
  sub,
}) => (
  <div className="mb-8">
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-willard-taupe flex items-center gap-2">
      {icon}
      {eyebrow}
    </p>
    <h2 className="font-heading text-4xl sm:text-5xl tracking-wide uppercase mt-1">{title}</h2>
    {sub && <p className="text-willard-grey-400 mt-2 max-w-2xl">{sub}</p>}
  </div>
);

const Delta: React.FC<{ value: number | null }> = ({ value }) =>
  value !== null && Math.round(value) !== 0 ? (
    <span className={`inline-flex items-center gap-1 font-bold text-sm ${value > 0 ? 'text-green-400' : 'text-willard-grey-400'}`}>
      {value > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
      {value > 0 ? '+' : ''}
      {Math.round(value)}
    </span>
  ) : null;

// "A at X and B at Y" from the league lines on the cards.
const listLeagues = (lines: CurrentLeague[]): string => {
  const names = [...new Set(lines.map((l) => `${l.league} at ${l.center}`))];
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

export const Home: React.FC = () => {
  const [team, setTeam] = useState<TeamStatsDoc | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [recap, setRecap] = useState<SeasonRecapDoc | null>(null);
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [news, setNews] = useState<PublicContent[]>([]);

  useEffect(() => {
    getDoc(doc(db, 'teamStats', 'current'))
      .then((s) => s.exists() && setTeam(s.data() as TeamStatsDoc))
      .catch(() => {});

    // Last season, frozen by sync-stats.mjs (public read, named lines are published bowlers only)
    getDoc(doc(db, 'teamStats', `recap-${SEASON.recap.key}`))
      .then((s) => s.exists() && setRecap(s.data() as SeasonRecapDoc))
      .catch(() => {});

    // Published players with live league data (rules only allow published+consented)
    getDocs(query(collection(db, 'players'), where('published', '==', true), where('consentOnFile', '==', true)))
      .then((r) => {
        setPlayers(
          r.docs
            .map((d) => ({ id: d.id, ...d.data() }) as Player)
            // willardTeam guard: the program tracks other schools' bowlers too
            .filter((p) => p.willardTeam !== false && p.removed !== true)
        );
      })
      .catch(() => {});

    getDocs(query(collection(db, 'schedules'), orderBy('date', 'asc'), limit(24)))
      .then((r) => {
        const now = Date.now() - 24 * 3600 * 1000;
        const upcoming = r.docs
          .map((d) => ({ id: d.id, ...d.data() }) as ScheduleEvent & { date: { toDate?: () => Date } })
          .filter((e) => {
            const dt = typeof e.date?.toDate === 'function' ? e.date.toDate() : new Date(e.date as unknown as string);
            return dt.getTime() >= now;
          })
          .slice(0, 4) as unknown as ScheduleEvent[];
        setEvents(upcoming);
      })
      .catch(() => {});

    getDocs(query(collection(db, 'publicContent'), where('isPublished', '==', true), limit(6)))
      .then((r) => setNews(r.docs.map((d) => ({ id: d.id, ...d.data() }) as PublicContent).slice(0, 3)))
      .catch(() => {});
  }, []);

  // One card per Tiger bowling the CURRENT season (Junior/Major 26-27 etc.), with
  // their latest league night. Sorted by fall average, highest first (DC 2026-09-09);
  // the delta vs last season's book is shown but does not affect order.
  const seasonCards = players
    .map((p) => {
      const line = currentSeasonLine(p);
      if (!line) return null;
      const book = p.lastSeasonBookAverage || 0; // USBC composite book, not the Stats-app blend
      const weeks: RecentWeek[] = (p.recentLeagueWeeks || []).filter((w) => w.league === line.league);
      const week: RecentWeek | null = weeks[weeks.length - 1] || null;
      return { p, line, weeks, week, delta: book > 0 ? line.currentAverage - book : null };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null)
    .sort((a, b) => b.line.currentAverage - a.line.currentAverage || b.line.games - a.line.games);
  // The one highlight that isn't about average: the biggest gain vs last season's
  // book gets a "Most Improved" chip. Cards stay sorted by average, so the chip is
  // what pulls the eye down the page to a developing bowler.
  const mostImproved = [...seasonCards].filter((c) => (c.delta ?? 0) > 0).sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0))[0] || null;
  const weekNo = Math.max(0, ...seasonCards.map((c) => c.week?.week ?? 0));
  const latestNight = seasonCards.map((c) => c.week?.date || '').sort().slice(-1)[0] || '';

  return (
    <div className="bg-willard-black text-white">
      {/* ---------- Hero ---------- */}
      <section className="relative overflow-hidden">
        <img
          src="/assets/logos/tiger-logo.jpg"
          alt=""
          aria-hidden
          className="absolute -right-24 top-1/2 -translate-y-1/2 w-[36rem] max-w-none opacity-[0.07] mix-blend-screen invert pointer-events-none select-none"
        />
        <div className="max-w-6xl mx-auto px-5 pt-16 sm:pt-20 pb-14 relative">
          <p className="text-xs sm:text-sm font-bold text-willard-taupe tracking-[0.25em] uppercase mb-4">
            Willard High School · Willard, MO · {SEASON.current.label} Season
          </p>
          <h1 className="font-display text-5xl sm:text-7xl md:text-8xl leading-[0.95]">
            TIGERS
            <br />
            BOWLING
          </h1>
          <p className="mt-6 max-w-xl text-willard-grey-400 text-base sm:text-lg">
            Every frame tracked. Every average verified. The {SEASON.current.label} season is underway. Watch this
            team get better week by week.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/roster"
              className="px-6 sm:px-7 py-3.5 bg-willard-taupe text-willard-black font-bold rounded-lg hover:bg-white transition-colors inline-flex items-center gap-2"
            >
              Meet the Team <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/schedule"
              className="px-6 sm:px-7 py-3.5 border border-willard-grey-700 text-white font-bold rounded-lg hover:border-willard-taupe transition-colors"
            >
              Schedule
            </Link>
          </div>
        </div>

        {/* Team stat band — real numbers or nothing */}
        {team && team.totalGames > 0 && (
          <div className="border-y border-willard-grey-800">
            <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-px bg-willard-grey-800">
              <Stat label="Team Average" value={team.teamAverage} />
              <Stat label="High School Bowlers" value={team.activePlayers} />
              <Stat label="Games Tracked" value={team.totalGames.toLocaleString()} />
              <Stat label="League High Game" value={team.highGame} />
            </div>
            <p className="max-w-6xl mx-auto px-5 py-3 text-xs text-willard-grey-500">
              Team average and high game come from sanctioned league play (LeagueSecretary + USBC), updated nightly.
              Games tracked frame by frame in our Stats app.
            </p>
          </div>
        )}
      </section>

      {/* ---------- Season Watch (current season) ---------- */}
      {seasonCards.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 py-14 sm:py-16">
          <SectionHead
            icon={<Flame className="w-4 h-4" />}
            eyebrow={`${SEASON.current.title}${weekNo ? ` · Week ${weekNo}` : ''}${latestNight ? ` · ${weekDate(latestNight)}` : ''}`}
            title="Fall Watch"
            sub={`${seasonCards.length} Tigers bowling ${listLeagues(seasonCards.map((c) => c.line))}. Averages update from LeagueSecretary after every league night. The colored number is the change vs last season's book average.`}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {seasonCards.map(({ p, line, weeks, delta }) => (
              <div
                key={p.id}
                className="bg-willard-grey-900 rounded-xl p-5 border border-willard-grey-800 hover:border-willard-taupe transition-colors group"
              >
                <Link to={`/player/${p.id}`} className="block">
                <div className="flex justify-between items-start">
                  <div className="min-w-0">
                    <p className="font-bold text-lg group-hover:text-willard-taupe transition-colors truncate">{p.name}</p>
                    <p className="text-xs text-willard-grey-500 truncate">
                      {line.league} · {line.center}
                      {line.team ? ` · ${line.team}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Delta value={delta} />
                    {mostImproved?.p.id === p.id && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-willard-taupe text-willard-black text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                        <Sparkles className="w-3 h-3" /> Most Improved
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-end gap-6 mt-4">
                  <div>
                    <p className="font-display text-4xl">{line.currentAverage}</p>
                    <p className="text-[11px] uppercase tracking-widest text-willard-grey-500">{SEASON.current.avgLabel}</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl text-willard-grey-400">{line.highGame}</p>
                    <p className="text-[11px] uppercase tracking-widest text-willard-grey-500">High Game</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl text-willard-grey-400">{line.games}</p>
                    <p className="text-[11px] uppercase tracking-widest text-willard-grey-500">Games</p>
                  </div>
                </div>
                </Link>
                {/* Latest night by default; "All N weeks" opens the season, newest first. */}
                <WeekLines weeks={weeks} tone="dark" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------- Last season recap ---------- */}
      {recap && recap.bowlers > 0 && (
        <section className="border-t border-willard-grey-800 bg-willard-grey-900/40">
          <div className="max-w-6xl mx-auto px-5 py-14 sm:py-16">
            <SectionHead
              icon={<Sun className="w-4 h-4" />}
              eyebrow={`${recap.label} · Recap`}
              title="Summer in the Books"
              sub={`Before the ${SEASON.current.label} season opened, ${recap.bowlers} Tigers bowled summer league at Sunshine Lanes and Enterprise Park. ${
                recap.improved > 0 ? `${recap.improved} of them finished above last season's book average.` : ''
              } Here's how it ended.`}
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-willard-grey-800 border border-willard-grey-800 rounded-xl overflow-hidden">
              <Stat label="Tigers" value={recap.bowlers} sub="in summer league" />
              <Stat label="Games" value={recap.games.toLocaleString()} sub="league games" />
              <Stat label="Team Avg" value={recap.teamAverage} sub="summer average" />
              <Stat label="High Game" value={recap.highGame.value} sub={recap.highGame.name || 'league play'} />
              <Stat label="High Series" value={recap.highSeries.value} sub={recap.highSeries.name || '3 games'} />
              {recap.biggestGain ? (
                <Stat
                  label="Biggest Gain"
                  value={`${recap.biggestGain.value > 0 ? '+' : ''}${recap.biggestGain.value}`}
                  sub={recap.biggestGain.name || 'vs book average'}
                />
              ) : (
                <Stat label="Avg Gain" value={recap.avgGain ?? '—'} sub="vs book average" />
              )}
            </div>
            {recap.lines.length > 0 && (
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-1">
                {[...recap.lines]
                  .sort((a, b) => b.average - a.average || b.games - a.games)
                  .map((l, i) => (
                  <li key={l.playerId} className="border-b border-willard-grey-800/80">
                    <Link
                      to={`/player/${l.playerId}`}
                      className="flex items-center gap-3 py-2.5 hover:text-willard-taupe transition-colors group"
                    >
                      <span className={`font-display text-lg w-6 ${i === 0 ? 'text-willard-taupe' : 'text-willard-grey-600'}`}>{i + 1}</span>
                      <span className="min-w-0">
                        <span className="font-bold block truncate">{l.name}</span>
                        <span className="text-xs text-willard-grey-500 block truncate">
                          {l.league}
                          {l.highGame ? ` · HG ${l.highGame}` : ''}
                          {l.highSeries ? ` · HS ${l.highSeries}` : ''}
                        </span>
                      </span>
                      <span className="ml-auto font-display text-2xl tabular-nums">{l.average}</span>
                      <span className="w-12 text-right text-sm">
                        <Delta value={l.delta} />
                      </span>
                    </Link>
                  </li>
                  ))}
              </ul>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
              <p className="text-xs text-willard-grey-500">
                Final summer averages from LeagueSecretary. The colored number is the change vs each bowler's last-season
                USBC book average.
              </p>
              <Link to="/stats" className="inline-flex items-center gap-1 text-willard-taupe font-bold text-sm hover:text-white transition-colors">
                <Trophy className="w-4 h-4" /> Team stats <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ---------- Upcoming + News ---------- */}
      {(events.length > 0 || news.length > 0) && (
        <section className="border-t border-willard-grey-800">
          <div className="max-w-6xl mx-auto px-5 py-14 sm:py-16 grid md:grid-cols-2 gap-12">
            {events.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="w-6 h-6 text-willard-taupe" />
                  <h2 className="font-heading text-3xl tracking-wide uppercase">Up Next</h2>
                </div>
                <ul className="space-y-3">
                  {events.map((e) => (
                    <li key={e.id} className="bg-willard-grey-900 rounded-lg px-5 py-4 border border-willard-grey-800">
                      <p className="font-bold">{e.title}</p>
                      <p className="text-sm text-willard-grey-400">
                        {e.location}{e.opponent ? ` · vs ${e.opponent}` : ''}{e.time ? ` · ${e.time}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
                <Link to="/schedule" className="inline-flex items-center gap-1 mt-4 text-willard-taupe font-bold text-sm hover:text-white transition-colors">
                  Full schedule <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            )}
            {news.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Megaphone className="w-6 h-6 text-willard-taupe" />
                  <h2 className="font-heading text-3xl tracking-wide uppercase">Tiger News</h2>
                </div>
                <ul className="space-y-3">
                  {news.map((n) => (
                    <li key={n.id} className="bg-willard-grey-900 rounded-lg px-5 py-4 border border-willard-grey-800">
                      <p className="font-bold">{n.title}</p>
                      <p className="text-sm text-willard-grey-400 line-clamp-2">{n.content}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ---------- Quiet recruiting band ---------- */}
      <section className="border-t border-willard-grey-800">
        <div className="max-w-6xl mx-auto px-5 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-willard-grey-400 text-sm">
            College coach? Every athlete profile carries verified USBC records and frame-level stats.
          </p>
          <Link to="/contact" className="text-willard-taupe font-bold text-sm hover:text-white transition-colors inline-flex items-center gap-1">
            Contact Coach Collins <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};
